// The profile screen: badges, stats, and recent races for one racer — plus the
// optional "claim" upgrade (handle + recovery word) that lets a profile move to
// another device. Works before the profile has any server data too (everything
// shows locked / zero until the first score is posted).
import { useEffect, useState } from "react";
import { toBadgeViews } from "@shared/badges";
import type { BadgeView, ProfileView } from "@shared/profile";
import { formatMs, formatPct } from "@shared/scoring";
import { deleteProfile, getProfile } from "../lib/api";
import { removeProfile, type LocalProfile } from "../lib/profile";
import ClaimProfileForm from "../components/ClaimProfileForm";
import SceneBackground from "../components/brand/SceneBackground";
import Wordmark from "../components/brand/Wordmark";

const LOCKED_BADGES: BadgeView[] = toBadgeViews(new Map());

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border-[3px] border-ink bg-white p-3 text-center shadow-[0_4px_0_#1c2a63]">
      <div className="font-display text-2xl tabular-nums text-ink">{value}</div>
      <div className="mt-0.5 text-xs font-bold uppercase tracking-wide text-ink/45">
        {label}
      </div>
    </div>
  );
}

export default function Profile({
  profile,
  onHome,
}: {
  profile: LocalProfile;
  onHome: () => void;
}) {
  const [view, setView] = useState<ProfileView | null>(null);
  const [loading, setLoading] = useState(Boolean(profile.playerId));
  // Mirrors the claimed handle for the header (kept in sync via ClaimProfileForm).
  const [claimed, setClaimed] = useState(profile.handle);
  // Delete-this-racer flow (COPPA deletion right): two-step confirm, then a
  // server delete (when the profile has a token) + local removal.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");

  async function handleDelete() {
    setDeleting(true);
    setDeleteErr("");
    try {
      if (profile.playerToken) {
        await deleteProfile({ playerToken: profile.playerToken });
      }
      removeProfile(profile.localId);
      onHome();
    } catch (e) {
      setDeleteErr(e instanceof Error ? e.message : "Couldn't delete — try again.");
      setDeleting(false);
    }
  }

  useEffect(() => {
    if (!profile.playerId) return;
    let active = true;
    setLoading(true);
    getProfile({ playerId: profile.playerId })
      .then((r) => active && setView(r.profile))
      .catch(() => active && setView(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [profile.playerId]);

  const badges = view?.badges ?? LOCKED_BADGES;
  const earnedCount = badges.filter((b) => b.earned).length;
  const stats = view?.stats;

  return (
    <div className="relative min-h-full">
      <SceneBackground showSpeed={false} />
      <main className="mx-auto w-full max-w-lg px-4 pb-16 pt-4">
        <div className="mb-2 flex items-center justify-between">
          <button type="button" className="btn-chunk bg-white px-4 py-2 text-sm" onClick={onHome}>
            ← Home
          </button>
        </div>
        <div className="mb-4 flex justify-center">
          <Wordmark width={200} />
        </div>

        <div className="panel p-5 sm:p-7">
          {/* identity */}
          <div className="flex items-center gap-3">
            <span
              className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border-[3px] border-ink bg-sky-top/20 text-3xl"
              aria-hidden
            >
              {profile.avatar || "🙂"}
            </span>
            <div className="min-w-0">
              <h2 className="truncate font-display text-2xl text-ink">
                {view?.displayName ?? profile.displayName}
              </h2>
              <p className="text-sm font-semibold text-ink/55">
                {earnedCount}/{badges.length} badges
                {claimed ? ` · @${claimed}` : ""}
              </p>
            </div>
          </div>

          {/* stats */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <StatCard label="Races" value={String(stats?.totalRaces ?? 0)} />
            <StatCard label="Correct" value={String(stats?.totalCorrect ?? 0)} />
            <StatCard label="Perfect" value={String(stats?.perfectRaces ?? 0)} />
            <StatCard
              label="Best accuracy"
              value={stats ? formatPct(stats.bestAccuracy) : "—"}
            />
            <StatCard
              label="Fastest avg"
              value={stats?.fastestAvgMs ? formatMs(stats.fastestAvgMs) : "—"}
            />
            <StatCard label="Daily streak" value={String(stats?.dailyStreak ?? 0)} />
          </div>

          {/* badges */}
          <h3 className="mt-7 font-display text-xl text-ink">Badges</h3>
          {loading ? (
            <p className="py-4 text-center font-semibold text-ink/40">Loading…</p>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              {badges.map((b) => (
                <div
                  key={b.id}
                  className={`rounded-2xl border-[3px] p-3 text-center ${
                    b.earned
                      ? "border-ink bg-sunny/30"
                      : "border-dashed border-ink/20 bg-white opacity-60"
                  }`}
                  title={b.description}
                >
                  <div className={`text-3xl ${b.earned ? "" : "grayscale"}`} aria-hidden>
                    {b.earned ? b.emoji : "🔒"}
                  </div>
                  <div className="mt-1 text-xs font-bold text-ink">{b.name}</div>
                  <div className="mt-0.5 text-[11px] font-medium leading-tight text-ink/50">
                    {b.description}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* recent */}
          {view && view.recent.length > 0 && (
            <>
              <h3 className="mt-7 font-display text-xl text-ink">Recent races</h3>
              <ul className="mt-3 space-y-2">
                {view.recent.map((r, i) => (
                  <li
                    key={`${r.date}-${i}`}
                    className="flex items-center justify-between rounded-2xl border-[3px] border-ink/15 bg-white px-3 py-2 text-sm"
                  >
                    <span className="font-bold text-ink">
                      {r.mode === "daily" ? "📅 Daily" : "🏁 Solo"}
                      <span className="ml-1.5 font-semibold text-ink/45">{r.date}</span>
                    </span>
                    <span className="tabular-nums font-semibold text-ink/70">
                      {r.correctCount}/{r.finishLine} · {formatPct(r.accuracy)} ·{" "}
                      {formatMs(r.totalMs)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* claim / save across devices */}
          <div className="mt-7 rounded-2xl border-[3px] border-dashed border-ink/20 bg-sky-top/15 p-4">
            {!profile.playerToken && !claimed ? (
              <p className="text-sm font-semibold text-ink/60">
                Finish and post a race to start your profile — then you can save it to
                play on other devices.
              </p>
            ) : (
              <ClaimProfileForm profile={profile} onClaimed={setClaimed} />
            )}
          </div>

          {/* delete this racer (COPPA deletion right) */}
          <div className="mt-4 rounded-2xl border-[3px] border-dashed border-flame/30 bg-flame/[0.04] p-4">
            {!confirmDelete ? (
              <button
                type="button"
                className="text-sm font-bold text-flame hover:underline"
                onClick={() => setConfirmDelete(true)}
              >
                🗑 Delete this racer
              </button>
            ) : (
              <div>
                <p className="text-sm font-semibold text-ink/70">
                  Permanently delete{" "}
                  <span className="font-bold text-ink">{profile.displayName}</span>?
                  This erases its badges, stats, and leaderboard scores, and can't be
                  undone.
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    className="btn-chunk bg-white px-4 py-2 text-sm"
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-chunk bg-flame px-4 py-2 text-sm text-white"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? "Deleting…" : "Yes, delete"}
                  </button>
                </div>
                {deleteErr && (
                  <p className="mt-2 text-xs font-bold text-flame">{deleteErr}</p>
                )}
              </div>
            )}
            <p className="mt-2 text-[11px] font-medium text-ink/45">
              Parents: this removes the racer from our servers.{" "}
              <a href="/privacy" className="font-bold text-grape underline">
                Privacy &amp; your choices
              </a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
